import React, { useEffect, useState } from "react";
import Sidebar from "../../Components/Sidebar";
import { fetchInquiryData } from "../../redux/actions/contactusAction";
import { fetchDegreeNames } from "../../redux/actions/degreeActions";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx"; // Import xlsx library
import { saveAs } from "file-saver"; // Import file-saver library
import PageComponent from "../../Components/PageComponent";
import Page from "../../Components/Page";

const Inquiry = ({ startYear, endYear }) => {
  const dispatch = useDispatch();

  // State for filters and pagination
  const [alYear, setAlYear] = useState("");
  const [degreeProgram, setDegreeProgram] = useState("");
  const [pageSize, setPageSize] = useState(10); // Default page size set to 10
  const [currentPage, setCurrentPage] = useState(1); // Current page number

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const start = startYear || currentYear - 30; // Default: Last 30 years
    const end = endYear || currentYear; // Default: Current year
    const years = [];
    for (let year = end; year >= start; year--) {
      years.push(year);
    }
    return years;
  };

  const years = generateYears();

  // Handle filter changes
  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
  };

  // Fetch inquiry data
  const fetchData = () => {
    const filters = {
      page_size: pageSize,
      page: currentPage, // Include page number in the query
    };
    if (alYear) filters.al_year = alYear;
    if (degreeProgram) filters.degree_program = degreeProgram;

    dispatch(fetchInquiryData(filters));
  };

  // Apply filters
  const handleApplyFilter = () => {
    setCurrentPage(1); // Reset to first page on filter change
    fetchData();
  };

  // Clear filters
  const handleClearFilter = () => {
    setAlYear("");
    setDegreeProgram("");
    setPageSize(10); // Reset to default page size
    setCurrentPage(1); // Reset to the first page
    dispatch(fetchInquiryData({ page_size: 10, page: 1 })); // Fetch all data with default settings
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(inquiryList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inquiries");

    // Write to Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Create a Blob and save the file
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "inquiries.xlsx");
  };

  // Fetching state from Redux
  const { data: inquiryList = [], loading, error, total_pages, total_records } =
    useSelector((state) => state.ContactUs.contactInfo || {});

  const degreenames = useSelector((state) => state.degree.degrees.data) || [];

  useEffect(() => {
    dispatch(fetchDegreeNames());
  }, [dispatch]);

  console.log("degreeNames", degreenames);

  // Fetch inquiry data on component mount or when filters/pagination change
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  // Handle pagination change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return <div className="text-center text-gray-700">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-center text-red-600">
        Error: {error || "Failed to load inquiry data"}
      </div>
    );
  }

  return (
    <>
      <PageComponent>
        <style jsx>{`
          .grid-container {
            display: grid;
            grid-template-columns: 256px 1fr; /* Sidebar Width and rest of the content */
            grid-gap: 0px;
            width: 100vw;
            height: 100vh;
            overflow: hidden;
            margin: 0; /* Add this line */
            padding: 0; /* Add this line */
          }
          .content {
            overflow: auto; /* Enable scrolling on the content area */
            padding: 20px;
          }
          .table-container {
            overflow-x: auto;
            border: 1px solid #ccc;
          }
          .table-container::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .table-container::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          .table-container::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .table-container::-webkit-scrollbar-thumb:hover {
            background: #555;
          }

          .scrollable-cell {
            max-height: 50px; /* Adjust as needed */
            overflow-y: auto;
            word-break: break-word; /* break long words */
            padding: 8px;
            white-space: pre-line; /* respect line breaks */
          }
        `}</style>
        <div className="fixed top-0 left-0 w-64 h-full overflow-y-auto bg-white shadow-md">
          <Sidebar />
        </div>

        <div className="grid-container">
          <Sidebar />
          <div className="content">
            <div className="flex items-center space-x-2 text-3xl text-gray-500">
              <span> Contact Us </span>
              <span>/</span>
              <span className="text-black">Inquiry</span>
            </div>

            <div className="p-6 mt-6 bg-white rounded-lg shadow-md">
              <h2 className="text-2xl font-bold">Inquiry List</h2>
              <div className="grid grid-cols-3 gap-4 py-6 mt-6">
                {/* A/L Year Filter */}
                <div>
                  <label className="block text-gray-700">Select A/L Year</label>
                  <select
                    value={alYear}
                    onChange={handleFilterChange(setAlYear)}
                    className="w-full px-4 py-2 mt-4 border border-gray-300 rounded-md focus:outline-none"
                  >
                    <option className="text-gray-600" value="">
                      Select A/L Year
                    </option>
                    <option value="" disabled>
                      -- Select a Year --
                    </option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Degree Program Filter */}
                <div>
                  <label className="block text-gray-700">
                    Select Degree Program
                  </label>
                  <select
                    value={degreeProgram}
                    onChange={handleFilterChange(setDegreeProgram)}
                    className="w-full px-4 py-2 mt-4 border border-gray-300 rounded-md focus:outline-none"
                  >
                    <option className="text-gray-600" value="">
                      Select Degree Program
                    </option>
                    {degreenames.map((degree) => (
                      <option key={degree.id} value={degree.program_name}>
                        {degree.program_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Buttons */}
                <div className="flex justify-end py-4 mt-6 space-x-4">
                  <button
                    type="reset"
                    onClick={handleClearFilter}
                    className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Clear Filter
                  </button>
                  <button
                    type="submit"
                    onClick={handleApplyFilter}
                    className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>

              <div className="flex gap-6">
                <span>
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      const newSize = event.target.value;
                      setPageSize(newSize);
                      setCurrentPage(1); // Reset to the first page when page size changes
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </span>

                <div className="flex justify-end">
                  <button
                    onClick={handleExportToExcel}
                    className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Export Excel
                  </button>
                </div>
              </div>

              {/* Added a wrapper div with a class and style */}
              <div
                className="table-container"
                style={{ overflowX: "auto", border: "1px solid #ccc" }}
              >
                {/* Added a border for visibility */}
                <table
                  className="w-full bg-white border border-black rounded-lg"
                  style={{ minWidth: "1500px", tableLayout: "fixed" }} // Increased minWidth
                >
                  <thead className="bg-[#DBDADE]">
                    <tr>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "120px" }} // Increased column widths
                      >
                        Inquiry ID
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "150px" }}
                      >
                        First Name
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "150px" }}
                      >
                        Surname
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "130px" }}
                      >
                        Contact Number
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "200px" }}
                      >
                        Email
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "100px" }}
                      >
                        A/L Year
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "200px" }}
                      >
                        Degree Program
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "250px" }}
                      >
                        Message
                      </th>
                      <th
                        className="py-2 px-3 text-center bg-[#DBDADE] border-b whitespace-nowrap"
                        style={{ width: "150px" }}
                      >
                        Submission Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiryList.length > 0 ? (
                      inquiryList.map((inquiry, index) => (
                        <tr
                          key={inquiry.id || index}
                          className={`border-b ${
                            index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          }`}
                        >
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.inquiry_id || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.first_name || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.surname || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.contact_no || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.email || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.al_year || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.degree_program_id || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.message || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-center border-b whitespace-nowrap scrollable-cell">
                            {inquiry.submission_date
                              ? new Date(
                                  inquiry.submission_date
                                ).toLocaleDateString()
                              : "N/A"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="10"
                          className="px-6 py-4 font-medium text-center text-gray-600"
                        >
                          No inquiries available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-6">
                <div>
                  Showing {inquiryList.length} of {total_records} entries
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-400"
                        : "bg-[#92288C] text-white"
                    }`}
                  >
                    «
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-400"
                        : "bg-[#92288C] text-white"
                    }`}
                  >
                    ‹
                  </button>
                  <span>Page {currentPage}</span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === total_pages}
                    className={`px-3 py-2 rounded ${
                      currentPage === total_pages
                        ? "bg-gray-200 text-gray-400"
                        : "bg-[#92288C] text-white"
                    }`}
                  >
                    ›
                  </button>
                  <button
                    onClick={() => handlePageChange(total_pages)}
                    disabled={currentPage === total_pages}
                    className={`px-3 py-2 rounded ${
                      currentPage === total_pages
                        ? "bg-gray-200 text-gray-400"
                        : "bg-[#92288C] text-white"
                    }`}
                  >
                    »
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    </>
  );
};

export default Inquiry;